import { LightningElement, api, wire } from "lwc";
import classNames from "classnames";

import { getAccessToken } from "@devforce/trailhead-client-auth";
import * as env from "@devforce/trailhead-client-env";
import { postJSON } from "@devforce/trailhead-client-fetch";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { logger } from "@devforce/trailhead-client-logger";
import { trackUserActivity } from "common/track";
import { showToast } from "tds/toaster";

import * as locales from "./locales";

type RatingOption = {
  points: number;
  tooltipKey: string;
  tooltip?: string;
};

interface RatingAPIResponse {
  data: {
    rateBadge: {
      success: boolean;
    };
  };
}

const OPTIONS: RatingOption[] = [
  { points: 1, tooltipKey: "not_helpful" },
  { points: 2, tooltipKey: "somewhat_helpful" },
  { points: 3, tooltipKey: "neutral" },
  { points: 4, tooltipKey: "helpful" },
  { points: 5, tooltipKey: "very_helpful" },
];

export default class extends LightningElement {
  @api apiName: string | null = null;
  @api trackable: boolean | null = false;

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = {};

  private points: number = 0;
  private ratingsVisible: boolean = true;

  private async rate(points: number) {
    try {
      const accessToken = await getAccessToken();
      let body = JSON.stringify({
        query: `
          mutation {
            rateBadge(input: {
              name: "${this.apiName}"
              points: ${points}
              client: "th-web"
            })
            {
              success
            }
          }`,
        variables: {},
      });
      let resp = await postJSON<RatingAPIResponse>(
        env.get("EXPERIENCE_API_HOST"),
        body,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      let success: boolean = resp.data.rateBadge.success;
      if (!success) {
        throw new Error(`Could not rate badge "${this.apiName}"`);
      }
    } catch (error) {
      if (error instanceof Error) {
        logger.error(error.message);
      }
      throw new Error(`Could not rate badge "${this.apiName}"`);
    }
  }

  private get ratingsOptions(): RatingOption[] {
    return OPTIONS.map((option) => {
      return {
        ...option,
        className: classNames("icon", `icon--five_star`, {
          "icon--selected": option.points <= this.points,
        }),
        onClick: () => {
          // remove the ratings div so they can no loger rate the badge
          this.ratingsVisible = false;
          this.handleRateBadge(option.points);
        },
        onMouseEnter: () => {
          this.points = option.points;
        },
        tooltip: this.labels[option.tooltipKey],
      };
    });
  }

  private handleRateBadge(points: number): void {
    this.rate(points)
      .then(() => {
        showToast({ message: this.labels.thanks, variant: "success" });
        if (this.trackable) {
          trackUserActivity(this, "badge_rating_clicked", {
            module_api_name: this.apiName ?? "",
            points: this.points,
            pageLocation: "th-badge-rating",
          });
        }
      })
      .catch((error) => {
        if (error instanceof Error) {
          showToast({ message: this.labels.error, variant: "error" });
          // show the ratings div so they can try and rate the badge again
          this.ratingsVisible = true;
          logger.error(error.message);
        }
      });
  }

  private handleMouseLeave(): void {
    this.points = 0;
  }
}
